import * as React from 'react';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { ThreadType } from '../../../../models/Support/SupportEnums';
import { isValidEmail } from '../../../../models/utils/validationUtils';
import {
	useAddThreadMutation,
	useForwardThreadMutation,
	useSendThreadMutation,
} from '../../../../queries/Support/useSupportThreadMutations';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';
import type { ComposerMode, ComposerReplyType, PendingAttachment } from './types';

/** Parse comma-separated email string into a trimmed, non-empty array */
export const parseEmails = (value: string): string[] =>
	value
		.split(',')
		.map(e => e.trim())
		.filter(isValidEmail);

const ATTACHMENT_KEY_SEPARATOR = '\u0000';

interface PendingReplyThread {
	body: string;
	attachmentKey: string;
	threadId: string;
}

const getOptionalEmails = (value: string): string[] | undefined => {
	const emails = parseEmails(value);
	return emails.length > 0 ? emails : undefined;
};

const getCompletedAttachmentIds = (attachments: PendingAttachment[]): string[] =>
	attachments
		.filter(attachment => !attachment.uploading && !attachment.id.startsWith('temp-'))
		.map(attachment => attachment.id);

const getAttachmentKey = (attachmentIds: string[]): string => attachmentIds.join(ATTACHMENT_KEY_SEPARATOR);

const getPendingReplyThreadId = (
	pendingReplyThread: PendingReplyThread | null,
	body: string,
	attachmentKey: string
): string | undefined =>
	pendingReplyThread?.body === body && pendingReplyThread.attachmentKey === attachmentKey
		? pendingReplyThread.threadId
		: undefined;

const getAttachmentIdsParam = (attachmentIds: string[]): string[] | undefined =>
	attachmentIds.length > 0 ? attachmentIds : undefined;

interface UseComposerSendOptions {
	bccValue: string;
	ccValue: string;
	clearEditor: () => void;
	forwardThreadId?: string;
	forwardToValue: string;
	getEditorHtml: () => string;
	/** Note mentions come from the editor's mention nodes, so no body text is needed. */
	getMentionedUserIds?: () => string[];
	mode: ComposerMode;
	onAfterSend?: () => void;
	onModeChange: (mode: ComposerMode) => void;
	onReplyTypeChange: (type: ComposerReplyType) => void;
	onSelfAssign?: () => void;
	pendingAttachments: PendingAttachment[];
	/** Reply recipient, editable in the address fields; seeded from the ticket's contact email. */
	replyToValue: string;
	replyType: ComposerReplyType;
	setBccValue: React.Dispatch<React.SetStateAction<string>>;
	setCcValue: React.Dispatch<React.SetStateAction<string>>;
	setForwardToValue: React.Dispatch<React.SetStateAction<string>>;
	ticket: ISupportTicket;
	ticketId: string;
}

export const useComposerSend = ({
	bccValue,
	ccValue,
	clearEditor,
	forwardThreadId,
	forwardToValue,
	getEditorHtml,
	getMentionedUserIds,
	mode,
	onAfterSend,
	onModeChange,
	onReplyTypeChange,
	onSelfAssign,
	pendingAttachments,
	replyToValue,
	replyType,
	setBccValue,
	setCcValue,
	setForwardToValue,
	ticket,
	ticketId,
}: UseComposerSendOptions) => {
	// Keep persistence and delivery failures separate so an unsaved message is never described as saved.
	const [saveError, setSaveError] = React.useState<string | null>(null);
	const [sendError, setSendError] = React.useState(false);
	const [forwardError, setForwardError] = React.useState(false);
	// Set when a forward is attempted with no valid recipient email (the Forward
	// button only checks for non-blank text, so invalid-but-nonblank input reaches here).
	const [invalidRecipients, setInvalidRecipients] = React.useState(false);
	const [showSelfAssign, setShowSelfAssign] = React.useState(false);

	// Tracks which forward-flow note body was already persisted server-side.
	// If the user retries forward after a note-save success but forward failure,
	// we skip only when the body is unchanged to avoid creating duplicate notes.
	const pendingForwardNoteBodyRef = React.useRef<string | null>(null);
	const pendingReplyThreadRef = React.useRef<PendingReplyThread | null>(null);

	const addThread = useAddThreadMutation(ticketId);
	const sendThread = useSendThreadMutation(ticketId);
	const forwardThread = useForwardThreadMutation(ticketId);

	const failSave = (error: unknown) =>
		setSaveError(
			getApiErrorMessage(
				error,
				replyType === 'note' ? 'Failed to save your note. Try again.' : 'Failed to save. Try again.'
			)
		);

	const resetSendErrors = () => {
		setSaveError(null);
		setSendError(false);
		setInvalidRecipients(false);
	};

	const resetForwardComposer = () => {
		clearEditor();
		setForwardToValue('');
		setCcValue('');
		setBccValue('');
		pendingForwardNoteBodyRef.current = null;
		setForwardError(false);
		setInvalidRecipients(false);
		onModeChange('collapsed');
		onReplyTypeChange('reply');
	};

	const saveForwardNoteIfNeeded = async (editorHtml: string): Promise<boolean> => {
		if (!editorHtml.trim() || pendingForwardNoteBodyRef.current === editorHtml) return true;

		try {
			await addThread.mutateAsync({
				type: ThreadType.Note,
				body: editorHtml,
			});
			pendingForwardNoteBodyRef.current = editorHtml;
			return true;
		} catch (error) {
			failSave(error);
			return false;
		}
	};

	const handleForwardFailure = (error: unknown) => {
		if (pendingForwardNoteBodyRef.current) {
			setForwardError(true);
			return;
		}
		failSave(error);
	};

	const sendForward = async ({ to, threadId }: { to: string[]; threadId: string }): Promise<boolean> => {
		try {
			await forwardThread.mutateAsync({
				threadId,
				to,
				cc: getOptionalEmails(ccValue),
				bcc: getOptionalEmails(bccValue),
			});
		} catch (error) {
			handleForwardFailure(error);
			return false;
		}

		resetForwardComposer();
		return true;
	};

	const handleForwardSend = async (): Promise<boolean> => {
		const to = parseEmails(forwardToValue);
		if (to.length === 0) {
			setInvalidRecipients(true);
			return false;
		}
		if (!forwardThreadId) {
			failSave(undefined);
			return false;
		}

		const noteSaved = await saveForwardNoteIfNeeded(getEditorHtml());
		if (!noteSaved) return false;

		return sendForward({ threadId: forwardThreadId, to });
	};

	const saveReplyOrNoteThread = async (body: string, attachmentIds: string[], attachmentKey: string) => {
		const existingReplyThread =
			replyType === 'reply' ? getPendingReplyThreadId(pendingReplyThreadRef.current, body, attachmentKey) : undefined;

		if (existingReplyThread) {
			return { id: existingReplyThread };
		}

		const mentionedUserIds = replyType === 'note' ? (getMentionedUserIds?.() ?? []) : [];
		return addThread.mutateAsync({
			type: replyType === 'reply' ? ThreadType.AgentReply : ThreadType.Note,
			body,
			attachmentIds: getAttachmentIdsParam(attachmentIds),
			mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
		});
	};

	const sendReplyThread = async (
		threadId: string | undefined,
		body: string,
		attachmentKey: string
	): Promise<boolean> => {
		if (!threadId) {
			setSendError(true);
			return false;
		}

		pendingReplyThreadRef.current = { body, attachmentKey, threadId };
		try {
			await sendThread.mutateAsync({
				threadId,
				to: replyToValue.trim(),
				cc: getOptionalEmails(ccValue),
				bcc: getOptionalEmails(bccValue),
			});
		} catch {
			// The reply thread is already saved; keep pendingReplyThreadRef so a retry resends it
			// without creating a duplicate. Surface delivery-specific copy, not a save failure.
			setSendError(true);
			return false;
		}
		// CC is sticky — don't clear it. BCC is one-time.
		setBccValue('');
		pendingReplyThreadRef.current = null;
		return true;
	};

	const finishReplyOrNoteSend = () => {
		clearEditor();
		if (replyType === 'reply' && !ticket.assigneeId && onSelfAssign) {
			setShowSelfAssign(true);
		}
	};

	const handleReplyOrNoteSend = async (): Promise<boolean> => {
		const body = getEditorHtml();
		if (!body.trim()) return false;
		// Avoid persisting a reply that cannot be delivered to a valid recipient.
		if (replyType === 'reply' && !isValidEmail(replyToValue.trim())) {
			setInvalidRecipients(true);
			return false;
		}

		const attachmentIds = getCompletedAttachmentIds(pendingAttachments);
		const attachmentKey = getAttachmentKey(attachmentIds);

		let threadResult: { id: string } | Awaited<ReturnType<typeof addThread.mutateAsync>>;
		try {
			threadResult = await saveReplyOrNoteThread(body, attachmentIds, attachmentKey);
		} catch (error) {
			// Nothing was persisted — don't claim the reply/note was saved.
			failSave(error);
			return false;
		}

		if (replyType === 'reply') {
			const sent = await sendReplyThread(threadResult?.id, body, attachmentKey);
			if (!sent) return false;
		}

		finishReplyOrNoteSend();
		return true;
	};

	/** Sends the current reply/note/forward. Returns true on success, false on failure. */
	const handleSend = async (): Promise<boolean> => {
		resetSendErrors();
		return replyType === 'forward' ? handleForwardSend() : handleReplyOrNoteSend();
	};

	const handleSendAndClose = async () => {
		const success = await handleSend();
		if (success) {
			onAfterSend?.();
		}
	};

	const isSending = addThread.isPending || sendThread.isPending || forwardThread.isPending;

	// Clear the pending forward-note body whenever the user leaves the forward flow
	// (composer collapsed, switched to reply/note). This guarantees a fresh forward
	// session always starts the note save afresh.
	React.useEffect(() => {
		if (replyType !== 'forward' || mode === 'collapsed') {
			pendingForwardNoteBodyRef.current = null;
			setForwardError(false);
			setInvalidRecipients(false);
		}
		if (replyType !== 'reply' || mode === 'collapsed') {
			pendingReplyThreadRef.current = null;
		}
	}, [replyType, mode]);

	return {
		forwardError,
		handleSend,
		handleSendAndClose,
		invalidRecipients,
		isSending,
		saveError,
		sendError,
		setForwardError,
		setInvalidRecipients,
		setSaveError,
		setSendError,
		setShowSelfAssign,
		showSelfAssign,
	};
};
